/**
 * Breathwork's own vocabulary, mapped onto the panel-wide design tokens in
 * component/common/designSystem/theme.js.
 *
 * Only domain meaning lives here — palettes, chart defaults and the chip
 * classes themselves belong to the design system.
 */
import { CHART_COLORS, toneChip, toneColor, toneLookup } from '../../../component/common/designSystem';

// Breath Hold vs Breath Exhale keep these two colours everywhere they appear,
// so a reader learns the mapping once.
export const SESSION_TYPE_COLORS = [CHART_COLORS.brand, CHART_COLORS.pink];

const sessionTypeTone = (type) => {
  if (type === 'BREATH_HOLD') return 'brand';
  if (type === 'BREATH_EXHALE') return 'accent';
  return 'neutral';
};

export const sessionTypeLabel = (type) => {
  if (type === 'BREATH_HOLD') return 'Breath Hold';
  if (type === 'BREATH_EXHALE') return 'Breath Exhale';
  return null;
};

export const sessionTypeChipClass = (type) => toneChip(sessionTypeTone(type));

// Experience labels come from the user's breathwork profile. The backend
// sends them as New / Few Months / 1+ Year, so those are the keys that have to
// match; the beginner/intermediate/advanced aliases keep older payloads and
// the user-detail chips reading the same colours.
const experienceTone = toneLookup({
  new: 'teal',
  'few months': 'info',
  '1+ year': 'accent',
  beginner: 'teal',
  intermediate: 'info',
  advanced: 'accent'
});

export const experienceColor = (label) => toneColor(experienceTone(label));
export const experienceChipClass = (label) => toneChip(experienceTone(label));

/**
 * Ordered ramps for the distribution charts. Each is a sequence rather than a
 * set of categories, so they get a ramp instead of the categorical palette —
 * and each has exactly as many colours as its band list has bands, so a slice
 * never borrows its neighbour's meaning.
 */

// Session length, Under 30s → 5 min+. A longer practice is a better one, so
// this is a plain shallow → deep ramp.
export const SESSION_LENGTH_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

// Sessions logged in the last 30 days: fewest → most.
export const CONSISTENCY_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.greenDeep
];

// Current streak, No streak → 15+ days.
export const STREAK_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

// Personal-best hold / exhale bands, Under 15s → 90s+.
export const BREATH_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.green,
  CHART_COLORS.greenDeep
];

// Early Morning → Night, running dawn → dusk so the axis reads as a day.
export const TIME_OF_DAY_RAMP = [
  CHART_COLORS.amber,
  CHART_COLORS.orange,
  CHART_COLORS.teal,
  CHART_COLORS.brand,
  CHART_COLORS.moss
];

// Library star ratings, Under 2★ → 4.5★+.
export const RATING_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.green,
  CHART_COLORS.greenDeep
];
